/* istanbul ignore file */
import { useEffect, useState } from 'react';

type ScriptStatus = 'loading' | 'ready' | 'error';

interface UseScriptOptions {
  src: string;
  id: string;
  async?: boolean;
  defer?: boolean;
  type?: string;
  onLoad?: () => void;
  onError?: (error: Event | string) => void;
}

const scriptStatuses: { [key: string]: ScriptStatus } = {};

export const useScript = ({
  src,
  id,
  async = true,
  defer = false,
  type = 'text/javascript',
  onLoad,
  onError,
}: UseScriptOptions): ScriptStatus => {
  const [status, setStatus] = useState<ScriptStatus>(
    () => scriptStatuses[id] || 'loading',
  );

  useEffect(() => {
    let script = document.querySelector(
      `script[src="${src}"]`,
    ) as HTMLScriptElement;

    if (!script) {
      script = document.querySelector(`script#${id}`) as HTMLScriptElement;
    }

    if (script) {
      script.id = id;
      script.async = async;
      script.defer = defer;
      script.type = type;

      if (script.getAttribute('data-status') === 'ready') {
        setStatus('ready');
        return;
      }
    } else {
      script = document.createElement('script');
      script.src = src;
      script.id = id;
      script.async = async;
      script.defer = defer;
      script.type = type;
      script.setAttribute('data-status', 'loading');

      script.onload = () => {
        script.setAttribute('data-status', 'ready');
        scriptStatuses[id] = 'ready';
        setStatus('ready');
        onLoad?.();
      };

      script.onerror = (error) => {
        script.setAttribute('data-status', 'error');
        scriptStatuses[id] = 'error';
        setStatus('error');
        onError?.(error);
      };

      document.body.appendChild(script);
    }
  }, [src, id, async, defer, type, onLoad, onError]);

  return status;
};
